/**
 * Colour sums for what a workspace sends out under its own brand: the Room
 * email, an offer button inside an email, the email footer, the unsubscribe
 * page. An email has no CSS variables and no color-mix, so every shade is
 * worked out here as a plain hex value, and each one is checked to read.
 */

/** Flashroom's own colours, for a workspace that has not chosen any. */
export const DEFAULT_ACCENT = "#7a2338";
export const DEFAULT_SECONDARY = "#b0873a";

/** The near-black text sits in, and the dark ink a pale button carries. */
const INK = "#1c1a20";

/** A hex colour as lowercase #rrggbb, or null when it is not one. */
export function normalHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim().replace(/^#/, "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  return /^[0-9a-fA-F]{6}$/.test(full) ? `#${full.toLowerCase()}` : null;
}

function channels(hex: string): [number, number, number] {
  const h = normalHex(hex) ?? "#000000";
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** WCAG relative luminance of a hex colour, or null when it is not one. */
export function luminanceOf(hex: string): number | null {
  if (!normalHex(hex)) return null;
  const [r, g, b] = channels(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast between two colours, from 1 (none) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminanceOf(a) ?? 0, luminanceOf(b) ?? 0].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** `a` moved toward `b` by `t` (0 is `a`, 1 is `b`). */
export function mixHex(a: string, b: string, t: number): string {
  const [p, q] = [channels(a), channels(b)];
  const out = p.map((v, i) => Math.round(Math.max(0, Math.min(255, v + (q[i] - v) * t))));
  return `#${out.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The colour as text on a ground: itself when it already reads at 4.5:1, else
 * the nearest deeper (or, on a dark ground, lighter) shade of it that does. A
 * pale brand colour keeps its hue in a small label instead of vanishing.
 */
export function readableOn(color: string, ground: string): string {
  const toward = (luminanceOf(ground) ?? 1) > 0.4 ? "#000000" : "#ffffff";
  for (let step = 0; step <= 50; step += 1) {
    const shade = mixHex(color, toward, step / 50);
    if (contrastRatio(shade, ground) >= 4.5) return shade;
  }
  return toward;
}

/** White or dark ink on a coloured fill, whichever reads better. */
export function inkOn(fill: string): string {
  return contrastRatio("#ffffff", fill) >= contrastRatio(INK, fill) ? "#ffffff" : INK;
}

/** The two brand colours a sent thing is drawn from, as saved (unchecked). */
export interface BrandColours {
  accent?: unknown;
  brass?: unknown;
}

/** Every colour a branded email is drawn with. */
export interface EmailPalette {
  /** Behind everything: near-white with a trace of the accent. */
  ground: string;
  card: string;
  ink: string;
  body: string;
  muted: string;
  faint: string;
  border: string;
  chipBg: string;
  chipBorder: string;
  /** Small text in the accent, deepened until it reads on the card. */
  label: string;
  /** Small text in the secondary colour, deepened until it reads on the ground. */
  secondary: string;
  /** The accent exactly, as a button fill. */
  button: string;
  buttonText: string;
}

/**
 * The palette for a workspace's brand. Always a light email: a dark one is
 * recoloured unpredictably by the mail apps that force their own dark mode, so
 * the brand arrives through the accent and the secondary colour instead.
 */
export function emailPalette(brand?: BrandColours | null): EmailPalette {
  const accent = normalHex(brand?.accent) ?? DEFAULT_ACCENT;
  const secondary = normalHex(brand?.brass) ?? DEFAULT_SECONDARY;
  const ground = mixHex(accent, "#f6f6f8", 0.985);
  const card = "#ffffff";
  return {
    ground,
    card,
    ink: INK,
    body: "#46424b",
    muted: "#6d6873",
    faint: "#8f8a95",
    border: mixHex(accent, "#e4e3e8", 0.95),
    chipBg: mixHex(accent, "#fafafb", 0.97),
    chipBorder: mixHex(accent, "#e4e3e8", 0.92),
    label: readableOn(accent, card),
    secondary: readableOn(secondary, ground),
    button: accent,
    buttonText: inkOn(accent),
  };
}

/**
 * The colour as a fill white text reads on at `min`: itself when it already
 * does, else the nearest deeper shade of it that does.
 */
function fillForWhite(color: string, min = 4.5): string {
  const base = normalHex(color) ?? DEFAULT_ACCENT;
  for (let step = 0; step <= 50; step += 1) {
    const shade = mixHex(base, "#000000", step / 50);
    if (contrastRatio("#ffffff", shade) >= min) return shade;
  }
  return "#000000";
}

/** The colours the Room email is drawn with beyond the shared palette. */
interface RoomEmailColours {
  /** The solid band across the top: the second colour when white reads on it, else the accent, deepened. */
  band: string;
  /** "New in your Room" on the band: a light tint of the accent that reads there. */
  bandLabel: string;
  /** The button and the video pictures' Watch pill: the accent, deep enough for white words. */
  action: string;
}

/**
 * The Room email's strong colours. The band holds white words, so a gold or
 * pale second colour gives way to the accent, deepened well past the line.
 */
export function roomEmailColours(brand?: BrandColours | null): RoomEmailColours {
  const accent = normalHex(brand?.accent) ?? DEFAULT_ACCENT;
  const secondary = normalHex(brand?.brass) ?? DEFAULT_SECONDARY;
  const band = contrastRatio("#ffffff", secondary) >= 4.5 ? secondary : fillForWhite(accent, 7);
  const tint = mixHex(accent, "#ffffff", 0.55);
  let bandLabel = tint;
  for (let step = 0; step <= 50 && contrastRatio(bandLabel, band) < 4.5; step += 1) {
    bandLabel = mixHex(tint, "#ffffff", step / 50);
  }
  return { band, bandLabel, action: fillForWhite(accent, 4.5) };
}
